import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, NavLink, useLocation, Navigate } from "react-router-dom";
import { LayoutDashboard, CalendarDays, FileSpreadsheet, BarChart3, Users, Sheet } from "lucide-react";
import "@/App.css";
import Sidebar from "@/components/Sidebar";
import Dashboard from "@/pages/Dashboard";
import BookingList from "@/pages/BookingList";
import BookingDetail from "@/pages/BookingDetail";
import Analytics from "@/pages/Analytics";
import Operations from "@/pages/Operations";
import OperationsSheet from "@/pages/OperationsSheet";
import CategorySettings from "@/pages/CategorySettings";
import StaffManagement from "@/pages/StaffManagement";
import TagManagement from "@/pages/TagManagement";
import TableSchemaEditor from "@/pages/TableSchemaEditor";
import TableOperations from "@/pages/TableOperations";
import DailySummary from "@/pages/DailySummary";
import Invoices from "@/pages/Invoices";
import GridProto from "@/pages/GridProto";
import LiveGrid from "@/pages/LiveGrid";
import LiveSheet from "@/pages/LiveSheet";
import AssignmentRules from "@/pages/AssignmentRules";
import ProductCatalog from "@/pages/ProductCatalog";
import AuditLog from "@/pages/AuditLog";
import GarsonManagement from "@/pages/GarsonManagement";
import BackupPanel from "@/pages/BackupPanel";
import Reviews from "@/pages/Reviews";
import LoginPage from "@/pages/LoginPage";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";

const mobileNavItems = [
  { to: "/", icon: LayoutDashboard, label: "Ana Sayfa", exact: true },
  { to: "/sheet", icon: FileSpreadsheet, label: "Operasyon" },
  { to: "/bookings", icon: CalendarDays, label: "Rezler" },
  { to: "/live-sheet", icon: Sheet, label: "Canli Defter" },
];

function MobileBottomNav({ isGarson }) {
  const location = useLocation();
  const items = isGarson
    ? [{ to: "/live-sheet", icon: Sheet, label: "Canli Sayfa" }]
    : mobileNavItems;
  return (
    <nav className="mobile-bottom-nav" data-testid="mobile-bottom-nav">
      {items.map((item) => {
        const isActive = item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to);
        return (
          <NavLink key={item.to} to={item.to} end={item.exact} className={isActive ? "active" : ""} data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <item.icon />
            <span>{item.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

function AppContent() {
  const { user, loading, isSuperAdmin, isManager, isGarson } = useAuth();
  const [appEnv, setAppEnv] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem("sidebar_collapsed") === "true"; } catch { return false; }
  });

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem("sidebar_collapsed", String(next)); } catch {}
      return next;
    });
  };

  useEffect(() => {
    const API = window.location.origin;
    fetch(`${API}/api/status`).then(r => r.json()).then(d => setAppEnv(d)).catch(() => {});
    // Remove Emergent badge
    const removeBadge = () => { const b = document.getElementById('emergent-badge'); if (b) b.remove(); };
    removeBadge();
    const observer = new MutationObserver(removeBadge);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0E1A]">
        <div className="text-sm text-slate-500">Yukleniyor...</div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  const isPreview = appEnv && !appEnv.is_production;

  return (
    <BrowserRouter>
      <div className="dashboard-layout">
        <Sidebar collapsed={sidebarCollapsed} onToggle={toggleSidebar} />
        <div className="main-content" style={sidebarCollapsed ? { marginLeft: 56 } : undefined}>
          {isPreview && (
            <div className="bg-amber-500 text-white text-center py-1.5 px-4 text-xs font-medium" data-testid="preview-banner">
              PREVIEW ORTAMI — Veritabani baglantisi devre disi. Production icin megalufer.org kullanin.
            </div>
          )}
          <Routes>
            {isGarson ? (
              <>
                <Route path="/live-sheet" element={<LiveSheet />} />
                <Route path="*" element={<Navigate to="/live-sheet" replace />} />
              </>
            ) : (
              <>
                <Route path="/" element={<Dashboard />} />
                <Route path="/bookings" element={<BookingList />} />
                <Route path="/bookings/:id" element={<BookingDetail />} />
                <Route path="/analytics" element={<Analytics />} />
                <Route path="/operations" element={<Operations />} />
                <Route path="/sheet" element={<OperationsSheet />} />
                <Route path="/category-settings" element={<CategorySettings />} />
                <Route path="/table-layout" element={<TableOperations />} />
                <Route path="/gunluk-ozet" element={<DailySummary />} />
                <Route path="/faturalar" element={<Invoices />} />
                <Route path="/table-schema" element={<TableSchemaEditor />} />
                <Route path="/grid-proto" element={<GridProto />} />
                <Route path="/live-grid" element={<LiveGrid />} />
                <Route path="/live-sheet" element={<LiveSheet />} />
                <Route path="/garsonlar" element={<GarsonManagement />} />
                <Route path="/backups" element={<BackupPanel />} />
                {/* Sadece Super Admin: personel, etiketler, dağıtım kuralları, yorumlar */}
                {isSuperAdmin ? (
                  <>
                    <Route path="/staff" element={<StaffManagement />} />
                    <Route path="/tags" element={<TagManagement />} />
                    <Route path="/rules" element={<AssignmentRules />} />
                    <Route path="/product-catalog" element={<ProductCatalog />} />
                    <Route path="/audit" element={<AuditLog />} />
                    <Route path="/reviews" element={<Reviews />} />
                  </>
                ) : (
                  <>
                    <Route path="/staff" element={<Navigate to="/" replace />} />
                    <Route path="/tags" element={<Navigate to="/" replace />} />
                    <Route path="/rules" element={<Navigate to="/" replace />} />
                    <Route path="/audit" element={<Navigate to="/" replace />} />
                    <Route path="/reviews" element={<Navigate to="/" replace />} />
                  </>
                )}
                <Route path="*" element={<Navigate to="/" replace />} />
              </>
            )}
          </Routes>
        </div>
        <MobileBottomNav isGarson={isGarson} />
      </div>
      <Toaster position="top-right" />
    </BrowserRouter>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
